import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '../data';
import Navigation from '../components/organisms/Navigation';
import Footer from '../components/organisms/Footer';
import PageTransition from '../components/templates/PageTransition';
import SEO from '../components/utils/SEO';

const WorkDetail = () => {
    const { id } = useParams();
    const project = PROJECTS.find(p => p.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!project) {
        return <div className="h-screen bg-[#050505] text-white flex items-center justify-center">Project not found</div>;
    }

    return (
        <PageTransition>
            <SEO
                title={project.title}
                description={project.description}
            />
            <div className="bg-[#050505] text-[#fbfbf4] font-sans selection:bg-[#fbfbf4] selection:text-[#050505] min-h-screen">
                <Navigation />

                <main className="relative z-10 pt-24 pb-0 overflow-hidden">
                    {/* TOP TITLE */}
                    <div className="px-4 md:px-8 mb-4 border-b border-white/10 pb-4 flex justify-between items-end">
                        <span className="text-xs uppercase tracking-[0.2em] opacity-50">{project.category}</span>
                        <div className="text-xs uppercase tracking-[0.2em] space-x-8 hidden md:block">
                            <span>{project.year}</span>
                            <span>{project.client || project.title}</span>
                        </div>
                    </div>
                    <div className="px-2 md:px-6">
                        <h1 className="font-sans font-black text-[12vw] leading-[0.8] tracking-tighter uppercase text-center md:text-left mb-8">
                            {project.title}
                        </h1>
                    </div>

                    {/* BENTO GRID */}
                    <section className="px-4 md:px-8 mb-20">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            {/* Left Col - Vertical (Span 4) */}
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                className="md:col-span-4 relative h-[60vh] md:h-auto bg-[#111] overflow-hidden rounded-sm group border border-white/10"
                            >
                                <img
                                    src={project.showcase?.[0] || project.image}
                                    alt=""
                                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                                    fetchPriority="high"
                                />
                                <div className="absolute bottom-4 left-4">
                                    <p className="text-xs uppercase tracking-widest bg-black/50 backdrop-blur-md px-2 py-1">Main View</p>
                                </div>
                            </motion.div>

                            {/* Right Col - Mixed (Span 8) */}
                            <div className="md:col-span-8 grid grid-cols-2 gap-4">
                                {/* Top Wide Image */}
                                <motion.div
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    className="col-span-2 aspect-video bg-[#111] overflow-hidden rounded-sm group border border-white/10"
                                >
                                    <img
                                        src={project.image}
                                        alt=""
                                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                                        fetchPriority="high"
                                    />
                                </motion.div>

                                {/* Bottom Left Small */}
                                <motion.div
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                    className="col-span-1 aspect-square bg-[#111] overflow-hidden rounded-sm group border border-white/10"
                                >
                                    <img
                                        src={project.showcase?.[1] || project.image}
                                        alt=""
                                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </motion.div>

                                {/* Bottom Right - Description Block */}
                                <div className="col-span-1 bg-[#111] p-6 md:p-8 rounded-sm flex flex-col justify-between border border-white/10">
                                    <div>
                                        <h3 className="text-xs uppercase tracking-[0.2em] opacity-50 mb-4">Overview</h3>
                                        <p className="text-sm md:text-base leading-relaxed opacity-80 line-clamp-6">
                                            {project.description}
                                        </p>
                                    </div>
                                    <a href={project.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest mt-4 hover:underline">
                                        View Live <ArrowUpRight size={14} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* PROJECT DETAILS (Challenge/Solution) - Compact */}
                    <section className="px-4 md:px-8 mb-32">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-white/10 pt-12">
                            <div>
                                <h3 className="text-2xl font-migra mb-4">The Challenge</h3>
                                <p className="text-sm opacity-70 leading-relaxed max-w-md">{project.challenge}</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-migra mb-4">The Solution</h3>
                                <p className="text-sm opacity-70 leading-relaxed max-w-md">{project.solution}</p>
                            </div>
                        </div>
                    </section>

                    {/* FULL LAYOUT (If exists) */}
                    {project.fullImage && (
                        <section className="px-4 md:px-8 mb-20 flex justify-center">
                            <div className="w-full max-w-7xl border border-white/10 rounded-sm overflow-hidden p-2 md:p-4 bg-[#111]">
                                <img
                                    src={project.fullImage}
                                    alt="Full Layout"
                                    className="w-full h-auto"
                                    loading="lazy"
                                />
                            </div>
                        </section>
                    )}

                    {/* PROCESS */}
                    {project.process && (
                        <section className="px-4 md:px-8 mb-20">
                            <h3 className="font-migra text-4xl mb-8">Process</h3>
                            <div className="grid grid-cols-1 gap-4">
                                {project.process.map((step, i) => (
                                    <div key={i} className="border border-white/10 p-6 md:p-8 rounded-sm">
                                        <span className="text-xs font-mono opacity-40 block mb-2">{step.phase}</span>
                                        <p className="opacity-80">{step.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* FOOTER NAV AREA */}
                    <section className="px-4 md:px-8 pb-12 relative">
                        <div className="border-t border-white/50 pt-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-12">
                            <div className="max-w-xl">
                                <h2 className="text-4xl md:text-6xl mb-6 font-migra">Let's build<br />something bold<br />together.</h2>
                                <Link to="/contact" className="w-16 h-16 rounded-full border border-white flex items-center justify-center hover:bg-white hover:text-black transition-colors">
                                    <ArrowUpRight size={24} />
                                </Link>
                            </div>

                            <div className="flex gap-12 text-xs uppercase tracking-[0.2em]">
                                {(() => {
                                    const currentIndex = PROJECTS.findIndex(p => p.id === id);
                                    const prevIndex = (currentIndex - 1 + PROJECTS.length) % PROJECTS.length;
                                    const nextIndex = (currentIndex + 1) % PROJECTS.length;
                                    const prevProject = PROJECTS[prevIndex];
                                    const nextProject = PROJECTS[nextIndex];

                                    return (
                                        <>
                                            <Link to={`/work/${prevProject.id}`} className="hover:opacity-50 transition-opacity text-right">
                                                <span className="block opacity-40 mb-2">Previous</span>
                                                {prevProject.title}
                                            </Link>
                                            <Link to={`/work/${nextProject.id}`} className="hover:opacity-50 transition-opacity text-right">
                                                <span className="block opacity-40 mb-2">Next</span>
                                                {nextProject.title}
                                            </Link>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* REPEATED HUGE TITLE BOTTOM */}
                        <div className="mt-20 overflow-hidden select-none pointer-events-none opacity-20">
                            <h1 className="font-sans font-black text-[18vw] leading-[0.7] tracking-tighter uppercase whitespace-nowrap text-center -ml-[10vw]">
                                {project.title}
                            </h1>
                        </div>
                    </section>

                    <Footer />
                </main>
            </div>
        </PageTransition>
    );
};

export default WorkDetail;
